import React, { useRef } from 'react'
import { Icon } from '../../Icons/Icon'
import { ReactComponent as CaretIcon } from '../../../images/Icon_Caret.svg'
import classNames from 'classnames'
import { createMultilineSentence } from '../../../helper/string'

export interface NavMenuButtonProps {
  active?: boolean;
  badge?: React.ReactNode;
  /**
   * Text to display on the button
   */
  label: string;
  className?: string;
  href?: string;
  hasAccordionCaret?: boolean;
  innerRef?: React.RefObject<any>;
  locationUrls?: Array<String>;
  /**
   * Click handler
   */
  onClick?: ()=> void
  icon?: string
}

export const NavMenuButton: React.FC<NavMenuButtonProps> = ({
  active,
  badge,
  className,
  label,
  href,
  hasAccordionCaret,
  innerRef,
  onClick,
  icon,
  locationUrls,
  ...props
}) => {
  const anchorRef:React.RefObject<HTMLAnchorElement> = useRef<HTMLAnchorElement>(null)
  const buttonRef:React.RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null)

  const pathname = window.location.pathname.split('?')[0].toLowerCase() // grab everything before any ?lang=es-mx query params should any exist
  const isPathInLocationUrls = locationUrls?.includes(pathname)

  const multilineElements = createMultilineElements(label, [
    { maxChars: 12, className: 'nav-text nav-text--1' },
    { maxChars: 16, className: 'nav-text nav-text--2' },
    { maxChars: 32, className: 'nav-text nav-text--3' }
  ])

  const iconLabelClass = classNames(`
    nav-menu-button-content-container ds-u-display--flex 
    ds-u-align-items--center ds-u-font-size--base ds-u-valign--bottom`,
  { 'nav-menu-button-content-container--active': active || isPathInLocationUrls })

  const iconAndLabel = () => {
    return (
      <>
        <span className={iconLabelClass}>
          {icon ? <Icon img={icon} text={label} /> : multilineElements}
        </span>
        {hasAccordionCaret && <CaretIcon className='ch-c-caret-fixed-size ch-u-margin-left--4px' />}
      </>
    )
  }

  if (href) {
    return (
      <a
        ref={anchorRef}
        className={'nav-menu-button ch-c-link ds-u-display--flex ds-u-font-size--base ds-u-sans'.concat(` ${className}`)}
        href={href}
        onClick={() => {
          if (anchorRef.current) {
            anchorRef.current.focus()
          }
          if (onClick) onClick()
        }}
      >
        {iconAndLabel()}
        {badge}
      </a>
    )
  } else {
    return (
      <button
        className='nav-menu-button ch-c-link ds-u-font-size--base ds-u-valign--bottom ds-u-sans'
        onClick={() => {
          if (innerRef && innerRef.current) {
            innerRef.current.focus()
          }
          if (onClick) onClick()
        }}
        type='button'
        aria-expanded={active}
        ref={innerRef}
        // ref={buttonRef}
      >
        {iconAndLabel()}
        {badge}
      </button>
    )
  }
}

// TODO - for more dynamic support, pass arrayOfMaxCharsWithClassList
// from "NavMenu" component by first measuring all NavMenuText label lengths,
// should "NavMenu" component have a props flag for requesting this calculation
// ...
// then calculating the total spacing width available to component
// pass in there a parameter "EN" or "ES" as well
// use a dictionary to reference a lookup for media queries needed and linebreak spaces
// you can also generate an inline <style> tag on element for javascript-generated-css-media-queries
function createMultilineElements (
  sentence: string,
  arrayOfMaxCharsWithClassList: Array<{ maxChars: number; className: string }>
): React.ReactNode[] {
  return arrayOfMaxCharsWithClassList.map((item, index) => {
    const labelText = createMultilineSentence(sentence, item.maxChars)
    return (
      <span className={item.className} key={index} aria-label={labelText} aria-hidden={index !== 0}>
        {labelText}
      </span>
    )
  })
};
